var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var UnitTest_1;
import { UnitTestInputToolTypes } from '@symon-ai/wisepipe-base-pipe';
import { Utils } from '@symon-ai/wisepipe-common';
import '@symon-ai/wisepipe-common-pipe'; // force import for JsonSchemaV2
import { OverrideType, ToolType, UnitTestDataOverride } from '@symon-ai/wisepipe-common-pipe';
import { log } from '@symon-ai/wisepipe-log';
import { DateConverter, JSON, JsonConverter, JsonSchemaV2, MapConverter } from '@symon-ai/wisepipe-serialize';
import './unitTestRun'; // force import for JsonSchemaV2
import { unitTestSchemaValidation } from '../validations.generated';
const NAME_MAX_LENGTH = 100;
const DESCRIPTION_MAX_LENGTH = 1000;
export const unitTestSchema = {
    $id: 'urn:wisepipe-common-computation:UnitTest',
    type: 'object',
    properties: {
        unitTestID: { type: 'string', uid: 9 },
        name: { type: 'string', minLength: 1, maxLength: NAME_MAX_LENGTH },
        description: { type: 'string', minLength: 1, maxLength: DESCRIPTION_MAX_LENGTH },
        isAIGenerated: { type: 'boolean' },
        pipeID: { type: 'string', uid: 10 },
        elementID: { type: 'string', uid: 10 },
        port: { type: 'string', minLength: 1 },
        createdAt: { type: 'string', format: 'date-time' },
        createdBy: { type: 'string', format: 'uuid' },
        lastModifiedAt: { type: 'string', format: 'date-time' },
        lastModifiedBy: { type: 'string', format: 'uuid' },
        dataMap: {
            type: 'object',
            additionalProperties: { $ref: 'urn:wisepipe-common-pipe:UnitTestDataOverride' }
        },
        snapshotLastUpdatedBy: { type: 'string', format: 'uuid' },
        snapshotLastUpdatedAt: { type: 'string', format: 'date-time' },
        result: { $ref: 'urn:wisepipe-common-computation:UnitTestRun' }
    },
    required: ['unitTestID', 'name', 'pipeID', 'elementID', 'port', 'createdAt', 'createdBy'],
    // if lastModifiedAt is provided, lastModifiedBy must also be provided
    allOf: [
        {
            if: { oneOf: [{ required: ['lastModifiedAt'] }, { required: ['lastModifiedBy'] }] },
            then: { required: ['lastModifiedAt', 'lastModifiedBy'] }
        },
        // if result is provided, snapshotLastUpdatedBy and snapshotLastUpdatedAt must also be provided
        {
            if: { oneOf: [{ required: ['snapshotLastUpdatedBy'] }, { required: ['snapshotLastUpdatedAt'] }] },
            then: { required: ['snapshotLastUpdatedBy', 'snapshotLastUpdatedAt'] }
        }
    ]
};
let UnitTest = UnitTest_1 = class UnitTest {
    constructor(unitTestID, name, pipeID, elementID, port, createdAt, createdBy, description, dataMap, isAIGenerated, lastModifiedAt, lastModifiedBy, snapshotLastUpdatedAt, snapshotLastUpdatedBy, result) {
        this.unitTestID = unitTestID;
        this.name = name;
        this.pipeID = pipeID;
        this.elementID = elementID;
        this.port = port;
        this.createdAt = createdAt;
        this.createdBy = createdBy;
        if (description !== undefined)
            this.description = description;
        if (dataMap !== undefined)
            this.dataMap = dataMap;
        if (isAIGenerated !== undefined)
            this.isAIGenerated = isAIGenerated;
        if (lastModifiedAt !== undefined)
            this.lastModifiedAt = lastModifiedAt;
        if (lastModifiedBy !== undefined)
            this.lastModifiedBy = lastModifiedBy;
        if (snapshotLastUpdatedAt !== undefined)
            this.snapshotLastUpdatedAt = snapshotLastUpdatedAt;
        if (snapshotLastUpdatedBy !== undefined)
            this.snapshotLastUpdatedBy = snapshotLastUpdatedBy;
        if (result !== undefined)
            this.result = result;
        JSON.validate(this, UnitTest_1);
    }
    isValid(pipeWithDependees, unitTestDataSources, originalDataSources, originalIncentivesDataSources) {
        if (this.dataMap === undefined)
            return false;
        // get all input type elements in the unit test pipe
        const inputElements = pipeWithDependees.elements.filter((element) => element.elementType === 'node' && UnitTestInputToolTypes.includes(element.toolType));
        // validate overrided columns from unit test datasource against columns in original data input against
        for (const element of inputElements) {
            // get original data columns
            const dataColumns = this.getOriginalColumns(element, pipeWithDependees, originalDataSources, originalIncentivesDataSources);
            // check if original data is mapped to a unit test data source
            if (!this.dataMap.has(element.elementID)) {
                log.error('No data override found for element', element.elementID);
                return false;
            }
            else {
                const unitTestDataOverride = this.dataMap.get(element.elementID);
                if (unitTestDataOverride?.overrideType === OverrideType.unitTestData) {
                    // check if column mapping is provided
                    if (!unitTestDataOverride?.columnOverrides)
                        return false;
                    // validate column mapping
                    const unitTestDataSource = unitTestDataSources.find((data) => data.dataSourceID === unitTestDataOverride?.unitTestDataSourceID);
                    if (!unitTestDataSource?.columns) {
                        throw new Error(`no columns found for unit test data source id: ${unitTestDataOverride?.unitTestDataSourceID}`);
                    }
                    const isColumnMapValid = this.isColumnMapValid(dataColumns, unitTestDataSource.columns, unitTestDataOverride?.columnOverrides);
                    if (!isColumnMapValid) {
                        log.error('Column mapping validation failed for element', element.elementID);
                        log.debug('comparison details:', {
                            dataColumns,
                            unitTestDataSource: unitTestDataSource.columns,
                            columnOverrides: unitTestDataOverride?.columnOverrides
                        });
                        return false;
                    }
                }
            }
        }
        return true;
    }
    isColumnMapValid(originalDataColumns, unitTestDataColumns, columnMap) {
        // check if all original columns are mapped to a valid column in unit test data source
        for (const originalColumn of originalDataColumns) {
            const unitTestColName = columnMap.find((map) => map.originalColName === originalColumn.name)?.unitTestDataColName;
            // if original column is not mapped to any column in unit test data source, return invalid
            if (!unitTestColName) {
                log.error('Original column is not mapped to any column in unit test data source:', originalColumn.name);
                return false;
            }
            // check if mapped column exists in unit test data source with matching col type
            if (!unitTestDataColumns.find((col) => col.name === unitTestColName && col.type === originalColumn.type)) {
                log.error('NOT mapped column exists in unit test data source with matching col type', originalColumn.name);
                return false;
            }
        }
        return true;
    }
    getOriginalColumns(inputElement, pipeWithDependees, originalDataSources, originalIncentivesDataSources) {
        let dataColumns;
        switch (inputElement.toolType) {
            case ToolType.FILE: {
                const originalDataSource = originalDataSources?.find((dataSource) => dataSource.dataSourceID === inputElement.config.fileID);
                if (!originalDataSource?.columns)
                    throw new Error(`Columns not found for original data source id: ${inputElement.config.fileID}`);
                dataColumns = originalDataSource.columns;
                break;
            }
            case ToolType.INCENTIVES_TABLE: {
                const originalDataSource = originalIncentivesDataSources?.find((dataSource) => dataSource.dataSourceID === inputElement.config.incentivesDataSourceID);
                if (!originalDataSource?.columns)
                    throw new Error(`Columns not found for original incentives data source id: ${inputElement.config.incentivesDataSourceID}`);
                dataColumns = originalDataSource.columns;
                break;
            }
            case ToolType.INCENTIVES_IMPORT: {
                const dependeePipe = pipeWithDependees.dependees?.find((dependeePipe) => dependeePipe.pipeID === inputElement.config.pipeID);
                const dependeeExportNode = dependeePipe?.elements.find((dependeeElement) => dependeeElement.elementID === inputElement.config.incentivesExportElementID);
                if (!dependeeExportNode)
                    throw new Error(`Incentives export node not found for id: ${inputElement.config.incentivesExportElementID}`);
                dataColumns = dependeeExportNode.config.exportColumns;
                break;
            }
            default:
                throw new Error(`unknown input tool type ${inputElement.toolType}`);
        }
        return dataColumns;
    }
    static newID() {
        return Utils.newID();
    }
    static bufferID(base64ID) {
        return Utils.base64StringToBuffer(base64ID);
    }
};
UnitTest.NAME_MAX_LENGTH = NAME_MAX_LENGTH;
UnitTest.DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
__decorate([
    JsonConverter(new DateConverter('date-time'))
], UnitTest.prototype, "createdAt", void 0);
__decorate([
    JsonConverter(new MapConverter(UnitTestDataOverride))
], UnitTest.prototype, "dataMap", void 0);
__decorate([
    JsonConverter(new DateConverter('date-time'))
], UnitTest.prototype, "lastModifiedAt", void 0);
__decorate([
    JsonConverter(new DateConverter('date-time'))
], UnitTest.prototype, "snapshotLastUpdatedAt", void 0);
UnitTest = UnitTest_1 = __decorate([
    JsonSchemaV2(unitTestSchema, unitTestSchemaValidation)
], UnitTest);
export { UnitTest };
//# sourceMappingURL=unitTest.js.map